import { GtmSupport } from "@gtm-support/vue2-gtm";
import { toProofreadingCharsBand } from "./charsBand";
import { AiProofreadingAnalyticsParams, ProofreadingAction } from "./types";

/**
 * 一括校正機能の Analytics クラス
 *
 * - 計測イベントは `proofreading_event` に統一し、`proofreading_action` でステップを識別する。
 * - ルート遷移のないダイアログ相当のフロー入口（タイプ選択画面）から手動で push する。
 * - `proofreading_chars` は生の文字数ではなく帯域文字列を送る。
 */
export class ProofreadingAnalytics {
  constructor(private readonly gtm: GtmSupport) {}

  trackOpen(params: { novelId: string }) {
    this.track("proofreading_open", {
      novel_id: params.novelId,
    });
  }

  trackRun(params: { novelId: string; chars: number }) {
    this.track("proofreading_run", {
      novel_id: params.novelId,
      proofreading_chars: toProofreadingCharsBand(params.chars),
    });
  }

  /**
   * Fish 実課金が成功したとき（無料再実行・課金 0 は送らない）。
   * `proofreading_run` の直後に、実際に subCoin したリクエストでのみ発火する。
   */
  trackPaid(params: { novelId: string; chars: number; fish: number }) {
    this.track("proofreading_paid", {
      novel_id: params.novelId,
      proofreading_chars: toProofreadingCharsBand(params.chars),
      proofreading_fish: params.fish,
    });
  }

  trackApply(params: { novelId: string; chars: number }) {
    this.track("proofreading_apply", {
      novel_id: params.novelId,
      proofreading_chars: toProofreadingCharsBand(params.chars),
    });
  }

  /** AI校正の原稿反映成功。基本校正の `proofreading_apply` とは分ける。 */
  trackAiApply(params: { novelId: string; chars: number }) {
    this.track("ai_proofreading_apply", {
      novel_id: params.novelId,
      proofreading_chars: toProofreadingCharsBand(params.chars),
    });
  }

  trackAiStart(params: { novelId: string }) {
    this.track("ai_proofreading_start", {
      novel_id: params.novelId,
    });
  }

  trackAiSubmit(params: AiProofreadingAnalyticsParams) {
    this.trackAi("ai_proofreading_submit", params);
  }

  trackAiComplete(params: AiProofreadingAnalyticsParams) {
    this.trackAi("ai_proofreading_complete", params);
  }

  trackAiCancelUi(params: Pick<AiProofreadingAnalyticsParams, "novelId" | "batchId">) {
    this.track("ai_proofreading_cancel_ui", {
      novel_id: params.novelId,
      ...(params.batchId ? { proofreading_batch_id: params.batchId } : {}),
    });
  }

  trackAiFail(params: Pick<AiProofreadingAnalyticsParams, "novelId" | "batchId">) {
    this.track("ai_proofreading_fail", {
      novel_id: params.novelId,
      ...(params.batchId ? { proofreading_batch_id: params.batchId } : {}),
    });
  }

  private trackAi(action: ProofreadingAction, params: AiProofreadingAnalyticsParams) {
    this.track(action, {
      novel_id: params.novelId,
      ...(params.totalChars != null
        ? { proofreading_chars: toProofreadingCharsBand(params.totalChars) }
        : {}),
      ...(params.checkCount != null ? { proofreading_check_count: params.checkCount } : {}),
      ...(params.estimatedFish != null ? { proofreading_fish: params.estimatedFish } : {}),
      ...(params.batchId ? { proofreading_batch_id: params.batchId } : {}),
      ...(params.method ? { proofreading_method: params.method } : {}),
    });
  }

  private track(action: ProofreadingAction, params: Record<string, unknown>) {
    this.push("proofreading_event", {
      proofreading_action: action,
      ...params,
    });
  }

  private push(event: string, payload: Record<string, unknown>) {
    const data = { event, ...payload };

    if (!this.gtm.enabled()) {
      console.warn(`[ProofreadingAnalytics] GTM disabled. skip event: ${event}`, data);
      return;
    }
    if (typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);

    if (process.env.NODE_ENV !== "production") {
      console.info("[ProofreadingAnalytics] dataLayer.push:", data);
    }
  }
}

export type { ProofreadingAction, AiProofreadingAnalyticsParams } from "./types";
export type { ProofreadingCharsBand } from "./charsBand";
export { toProofreadingCharsBand } from "./charsBand";
export type { ProofreadingMethod } from "./method";
export { toProofreadingMethod } from "./method";
export { ProofreadingAnalytics as default };
